import React, { useState } from 'react';
import {
  ButtonTertiary,
  cssColorValue,
  desktopMestoBold,
  IconChevronDown,
  IconChevronUp,
  NavBar,
  NavBarList,
  screenMdMin,
  semanticColors,
  spacer32,
  spacer8,
  TextLink,
} from '@spotify-internal/encore-web';
import styled from 'styled-components';

import { useTranslation } from 'providers/translations/TranslationProvider';
import { MastheadLinkList, MastheadLinkOrLinkListArray } from 'lib/sanity/types';
import { isMastheadLinkList } from 'utils/type-guards';
import useDropdown, { DropdownContext } from 'components/Masthead/Header/hooks/useDropdown';
import { NavBarPopover } from 'components/Masthead/Header/Popover';
import { PopoverLink } from 'components/Masthead/Header/types';

const StyledNavBar = styled(NavBar)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  @media (max-width: ${screenMdMin}) {
    display: none;
  }
`;

const StyledNavBarList = styled(NavBarList)`
  gap: ${spacer32};
  line-height: 24px;
  margin: 0;
  overflow-x: visible;
  padding: 0;
  ${desktopMestoBold};
`;

const NavLink = styled(TextLink)`
  cursor: pointer;
  ${desktopMestoBold};

  && {
    text-decoration: none;

    &:active,
    &:hover,
    &:focus {
      text-decoration: none;
    }

    &:focus {
      color: ${cssColorValue(semanticColors.textBrightAccent)};
    }
  }
`;

const NavBarPopoverButton = styled(ButtonTertiary)`
  color: ${cssColorValue(semanticColors.textBase)};
  cursor: pointer;
  padding: 0;
  min-block-size: auto;
  ${desktopMestoBold};

  && {
    text-decoration: none;

    &:active,
    &:hover,
    &:focus {
      transform: none;
    }

    &:hover,
    &:focus {
      color: ${cssColorValue(semanticColors.textBrightAccent)};
    }

    &::after {
      inline-size: 100%;
      bottom: -3px;
    }

    & > * {
      margin-inline-start: ${spacer8};
    }
  }
`;

const StyledIconChevronDown = styled(IconChevronDown)`
  transform: translateY(1px);
`;

const StyledIconChevronUp = styled(IconChevronUp)`
  transform: translateY(3px);
`;

const NavbarDropdown = ({ linkListEntry }: { linkListEntry: MastheadLinkList }) => {
  const { handlers, open } = useDropdown(linkListEntry.label);
  const { t } = useTranslation();
  const links = (linkListEntry.entries || []).map<PopoverLink>(linkListItem => {
    const link = linkListItem.link;
    return {
      href: link.href,
      linkText: link.title,
      level: linkListItem?.nestingLevel,
      target: link.target,
    };
  });

  return (
    <NavBarPopover
      isOpen={open}
      links={links}
      onMouseEnter={handlers.handleOnMouseEnter}
      onMouseLeave={handlers.handleOnMouseLeave}
    >
      <NavBarPopoverButton
        href=""
        iconTrailing={() =>
          open ? (
            <StyledIconChevronUp size="small" aria-label={t('navbar_dropdown_menu_close')} />
          ) : (
            <StyledIconChevronDown size="small" aria-label={t('navbar_dropdown_menu_open')} />
          )
        }
        onKeyDown={handlers.handleOnKeyDown}
        onMouseDown={handlers.handleOnMouseDown}
      >
        {linkListEntry.label}
      </NavBarPopoverButton>
    </NavBarPopover>
  );
};

type Props = {
  primaryLinks?: MastheadLinkOrLinkListArray;
};

export const NavBarPrimaryLinks = ({ primaryLinks }: Props) => {
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);

  if (!primaryLinks) {
    return null;
  }

  return (
    <StyledNavBar
      list={
        <nav dir="ltr">
          <StyledNavBarList>
            <DropdownContext.Provider value={{ activeDropdown, setActiveDropdown }}>
              {primaryLinks?.map((item, index) => {
                if (isMastheadLinkList(item)) {
                  return (
                    <li key={item.label + index}>
                      <NavbarDropdown linkListEntry={item} />
                    </li>
                  );
                }
                return (
                  <li key={index}>
                    <NavLink href={item.href} target={item.target}>
                      {item.title}
                    </NavLink>
                  </li>
                );
              })}
            </DropdownContext.Provider>
          </StyledNavBarList>
        </nav>
      }
    />
  );
};
